import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  AUTH_REFRESH_TOKEN_ENDPOINT,
  AUTH_CHANGE_PASSWORD_ENDPOINT,
  AUTH_CHANGE_PASSWORD_ON_FIRST_LOGIN_ENDPOINT,
  SUBSCRIPTION_EXTERNAL_PROVIDER_INITIALIZE_ENDPOINT,
  SUBSCRIPTION_CREATE_WITH_PAYMENT_ENDPOINT,
  SUBSCRIPTION_UPDATE_AUTORENEW_ENDPOINT,
  SUBSCRIPTION_CONFIRM_EPOSTA_REDIRECT_PAYMENT_ENDPOINT,
  PAYMENTS_TWOCHECKOUT_INITIATE_PATH,
  PAYMENTS_TWOCHECKOUT_STATUS_PATH_PREFIX,
} from '@core/constant/endpoints';
import { AuthService } from '@core/services/auth.service';
import { AuthActions } from '@core/store/auth/auth.actions';
import { ErrorTranslationService } from '@core/services/error-translation.service';
import { GlobalActions } from '@core/store/global/global.actions';
import { Store } from '@ngrx/store';
import {redirectExternalPricingOnSessionNotFound, isExternalPricingRoute,} from '@core/utils/external-pricing-session';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalStorageService } from '@core/services/local-storage.service';
import { LOCAL_STORAGE_KEY_AUTH_TOKEN, LOCAL_STORAGE_KEY_USER_NAME } from '@core/constant/constant';

/**
 * Interceptor that handles server errors.
 *
 * @param request The request object.
 * @param next The next interceptor in the chain.
 *
 * @returns The next Observable.
 */
export const serverErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const store = inject(Store);
  const handleError = inject(ErrorTranslationService);
  const localStorageService = inject(LocalStorageService);
  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      const onExternalPricing = isExternalPricingRoute(router.url);
      const isUnauthorized = [HttpStatusCode.Unauthorized, 0].includes(error.status);
      const isRefreshFailure = !!error.url?.includes(AUTH_REFRESH_TOKEN_ENDPOINT);
      const alreadyOnSessionNotFound = router.url.includes('/pricing/return-not-found');

      // Redis session missing/expired -shared not-found page.
      if (
        onExternalPricing &&
        error.status === HttpStatusCode.Unauthorized &&
        !isRefreshFailure &&
        !alreadyOnSessionNotFound
      ) {
        redirectExternalPricingOnSessionNotFound(router);
        return throwError(() => error);
      }

      if (error.error != null && !isRefreshFailure) {
        store.dispatch(
          GlobalActions.setMessage({
            infoMessage: {
              message: handleError.getMessageError(error),
              type: 'error',
            },
          })
        );
      }
      const isChangePasswordEndpoint = error.url?.includes(AUTH_CHANGE_PASSWORD_ENDPOINT) || 
                                       error.url?.includes(AUTH_CHANGE_PASSWORD_ON_FIRST_LOGIN_ENDPOINT);
      const isExternalProviderFlowUrl = (url: string | null | undefined) =>
        !!url &&
        (url.includes(SUBSCRIPTION_EXTERNAL_PROVIDER_INITIALIZE_ENDPOINT) ||
          url.includes(SUBSCRIPTION_CREATE_WITH_PAYMENT_ENDPOINT) ||
          url.includes(PAYMENTS_TWOCHECKOUT_INITIATE_PATH) ||
          error.url?.includes(SUBSCRIPTION_UPDATE_AUTORENEW_ENDPOINT) ||
          url.includes(SUBSCRIPTION_CONFIRM_EPOSTA_REDIRECT_PAYMENT_ENDPOINT) ||
          url.includes(PAYMENTS_TWOCHECKOUT_STATUS_PATH_PREFIX));

      if (onExternalPricing && (isRefreshFailure || isUnauthorized)) {
        if (isRefreshFailure) {
          store.dispatch(AuthActions.logout());
          localStorageService.removeData(LOCAL_STORAGE_KEY_AUTH_TOKEN);
          localStorageService.removeData(LOCAL_STORAGE_KEY_USER_NAME);
        }
        return throwError(() => error);
      }

      if (
        (error.status != HttpStatusCode.Ok && isRefreshFailure) ||
        (isUnauthorized &&
          !isChangePasswordEndpoint &&
          !isExternalProviderFlowUrl(error.url))
      ) {
        store.dispatch(AuthActions.logout());
        authService.logout();
        router.navigateByUrl('/login');
      }
      return throwError(() => error);
    })
  );
};
